import { createContext, useContext, useEffect, useMemo } from 'react';
import { ClerkProvider, useAuth, useClerk, useUser } from '@clerk/react-router';
import {
  ADMIN_CLERK_ENABLED,
  CLERK_AFTER_SIGN_OUT_URL,
  CLERK_ENABLED,
  CLERK_PUBLISHABLE_KEY,
  CLERK_SIGN_IN_ROUTE,
  WORKSPACE_CLERK_ENABLED,
} from '../../shared/utils/constants';
import {
  DISABLED_CLERK_RUNTIME,
  setClerkRuntime,
} from '../../shared/api/clerkRuntimeStore';

const ClerkRuntimeContext = createContext(DISABLED_CLERK_RUNTIME);

function ClerkRuntimeBridge({ children }) {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const clerk = useClerk();
  const { user } = useUser();

  const value = useMemo(
    () => ({
      enabled: true,
      workspaceEnabled: WORKSPACE_CLERK_ENABLED,
      adminEnabled: ADMIN_CLERK_ENABLED,
      isLoaded: Boolean(isLoaded),
      isSignedIn: Boolean(isSignedIn),
      user: user || null,
      getToken: async (options = {}) => getToken(options),
      signOut: async (options = {}) => clerk.signOut(options),
    }),
    [clerk, getToken, isLoaded, isSignedIn, user]
  );

  useEffect(() => {
    setClerkRuntime(value);
    return () => {
      setClerkRuntime(DISABLED_CLERK_RUNTIME);
    };
  }, [value]);

  return (
    <ClerkRuntimeContext.Provider value={value}>{children}</ClerkRuntimeContext.Provider>
  );
}

export function useClerkRuntime() {
  return useContext(ClerkRuntimeContext);
}

export default function ClerkRuntimeProvider({ children }) {
  const disabledRuntime = useMemo(
    () => ({
      ...DISABLED_CLERK_RUNTIME,
      workspaceEnabled: false,
      adminEnabled: false,
    }),
    []
  );

  useEffect(() => {
    if (!CLERK_ENABLED) {
      setClerkRuntime(disabledRuntime);
      return () => {
        setClerkRuntime(DISABLED_CLERK_RUNTIME);
      };
    }

    return undefined;
  }, [disabledRuntime]);

  if (!CLERK_ENABLED) {
    return (
      <ClerkRuntimeContext.Provider value={disabledRuntime}>
        {children}
      </ClerkRuntimeContext.Provider>
    );
  }

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      signInUrl={CLERK_SIGN_IN_ROUTE}
      afterSignOutUrl={CLERK_AFTER_SIGN_OUT_URL}
    >
      <ClerkRuntimeBridge>{children}</ClerkRuntimeBridge>
    </ClerkProvider>
  );
}
